import { computed } from "vue";
import { useStore } from "vuex";

import type { DeliverableIdentifier } from "@/enums/library";
import { CampaignGetters, type Getters } from "@/store/modules/campaign";
import type { RootState } from "@/store/types";

import type { ImportRow } from "../../../types/importExportTypes";
import type { LanguageCode } from "@/utils/languages";

export function useDeliverableImport() {
    const store = useStore<RootState>();

    const masterTemplatesBySize = computed(
        () =>
            (store.getters as Record<string, unknown>)[
                CampaignGetters.masterTemplatesBySize
            ] as Getters["masterTemplatesBySize"]
    );

    const groupValueNamesByGroup = computed(
        () =>
            (store.getters as Record<string, unknown>)[
                CampaignGetters.groupValueNamesByGroup
            ] as Getters["groupValueNamesByGroup"]
    );

    const validMasterTemplateLabels = computed(() => Object.keys(masterTemplatesBySize.value));
    const validLanguages = computed(() => store.state.campaign.languages);
    const editableGroupIds = computed(() => store.state.campaign.normalized.editableGroupIds);

    const metadataFieldIdByName = computed<Record<string, string>>(() =>
        Object.values(store.state.deliverableLibrary.metadataFieldsByFieldId).reduce<Record<string, string>>(
            (acc, field) => {
                acc[field.name] = field._id;

                return acc;
            },
            {}
        )
    );

    function getValidationError(row: ImportRow, groupNames: string[], rowNumber: number): string[] {
        const [rawSize, rawLanguage, ...values] = row;

        const size = rawSize ?? "";
        const language = rawLanguage ?? "";
        const errors: string[] = [];

        if (!validMasterTemplateLabels.value.includes(size)) {
            errors.push(`Invalid size at row ${rowNumber}`);
        }

        if (!validLanguages.value.includes(language as LanguageCode)) {
            errors.push(`Invalid language at row ${rowNumber}`);
        }

        values.forEach((value, i) => {
            const groupName = groupNames[i];
            if (groupName && !groupValueNamesByGroup.value[groupName]?.editableGroupValuesNames.includes(value ?? "")) {
                errors.push(`Invalid value "${value}" for group "${groupName}" at row ${rowNumber}`);
            }
        });

        return errors;
    }

    function processRow(row: ImportRow, groupNames: string[]): DeliverableIdentifier {
        const [rawSize, rawLanguage, ...values] = row;
        const size = rawSize ?? "";

        const editableGroupValueIds = values
            .map((value, i) => {
                const groupName = groupNames[i];

                return groupName
                    ? groupValueNamesByGroup.value[groupName]?.editableGroupValuesNamesMap?.[value ?? ""]
                    : undefined;
            })
            .filter((id): id is string => Boolean(id));
        // TODO: what to do with duplicated labels?
        const masterTemplateId = masterTemplatesBySize.value[size]?.[0] ?? "";

        return {
            language: rawLanguage ?? "",
            masterTemplateId,
            editableGroupValueIds
        };
    }

    return {
        editableGroupIds,
        metadataFieldIdByName,
        getValidationError,
        processRow
    };
}
